import { Button, Tooltip } from "antd"
import { CSSProperties, type ReactNode, useCallback, useState } from "react"

import { type ReactChild } from "#customTypes/globalTypes"

import Modal from "./Modal"

interface ButtonToggleModalProps {
  children: ReactChild
  modalTitle: string | ReactNode
  buttonLabel: string | ReactNode
  tooltip?: string
  isIcon?: boolean
  open?: boolean
  onToggle?: (newIsOpen?: boolean) => void
  width?: number
  onOk?: () => Promise<void> | void
  okText?: string
  size?: "small" | "default" | "large"
  buttonStyle?: CSSProperties
}

const ButtonToggleModal = ({
  children,
  buttonLabel,
  modalTitle,
  tooltip,
  isIcon,
  open,
  onToggle,
  onOk,
  okText,
  width,
  buttonStyle,
  size = "default",
}: ButtonToggleModalProps) => {
  const buttonSize = size === "default" ? "middle" : size
  const [isOpen, setIsOpen] = useState(open ?? false)

  const shouldShow = open != null ? open : isOpen

  const handleToggle = useCallback(() => {
    onToggle?.(!isOpen)
    setIsOpen(!isOpen)
  }, [setIsOpen, onToggle, isOpen])

  const handleOk = useCallback(async () => {
    try {
      await onOk?.()
      setTimeout(() => {
        handleToggle()
      }, 500)
    } catch (error) {
      // Don't log if it's a cancellation (user chose to stay in modal)
      if (
        error instanceof Error &&
        error.message.includes("Cancelling closing form")
      ) {
        return
      }
      console.error(error)
    }
  }, [onOk, handleToggle])

  let button = isIcon ? (
    <Button
      icon={buttonLabel}
      onClick={handleToggle}
      shape="circle"
      size={buttonSize}
      variant="text"
      color="default"
      style={buttonStyle}
    />
  ) : (
    <Button onClick={handleToggle} size={buttonSize} style={buttonStyle}>
      {buttonLabel}
    </Button>
  )

  if (tooltip) {
    button = <Tooltip title={tooltip}>{button}</Tooltip>
  }

  return (
    <>
      {button}
      <Modal
        onOk={handleOk}
        okText={okText}
        title={modalTitle}
        open={shouldShow}
        onClose={handleToggle}
        width={width}
      >
        {children}
      </Modal>
    </>
  )
}

export default ButtonToggleModal
